/**
 * Data mana yang dibaca crossplot Rw — sumur mana, dan kedalaman mana.
 *
 * Panel ini berdiri **di dalam** tab Rw & RO dan bukan di rel kiri layar, dan
 * itu bukan soal tempat melainkan soal pertanyaan yang berbeda. Rel kiri
 * memilih sumur yang **digolongkan**: ke sumur itulah kurva LRLC diterbitkan,
 * dan di sumur itulah tebal yang dilaporkan layar ini dijumlahkan. Rw bukan
 * hal semacam itu. Ia satu angka milik air formasi, dan yang paling sering
 * benar adalah membacanya dari kaki air **sumur-sumur di sekeliling** sumur
 * yang sedang dianalisis — termasuk sumur yang tidak sedang digolongkan sama
 * sekali. Memaksa keduanya memakai satu daftar berarti memaksa orang memilih
 * antara menggolongkan satu sumur dan membaca Rw yang bisa dipercaya.
 *
 * Tiga cara memilih sumurnya, dan ketiganya memang dipakai orang:
 *
 *     satu sumur     Rw sumur ini sendiri, kalau kaki airnya cukup panjang.
 *     beberapa sumur Daftar yang dipilih dengan tangan.
 *     di sekitarnya  Lingkaran berjari-jari sekian dari satu sumur acuan.
 *
 * Yang ketiga itu yang paling menuntut: sebuah lapangan berisi lima ratus
 * sumur tidak bisa dipilih dengan tangan, dan air formasi berubah menurut
 * tempat — Rw dari sumur empat puluh kilometer jauhnya adalah angka dari
 * akuifer yang lain. Jaringnya dipinjam dari `crossplot.screen` di server,
 * yaitu penjaring yang sama dengan yang dipakai crossplot dan histogram.
 *
 * Kedalamannya empat, dan MD berdiri sebagai modenya sendiri: sebuah selang
 * yang disebut dengan angka — "2.050 sampai 2.180" — adalah kedalaman yang
 * sama di tiap sumur, sedangkan marker dan zona berlaku di tiap sumur dengan
 * kedalamannya masing-masing. Keduanya pertanyaan yang berbeda, dan yang
 * menggabungkannya ke dalam satu kotak membuat orang mengira jawabannya sama.
 *
 * Selama belum disentuh, kedua pemilihnya **mengikuti layar** — dan itu
 * dikatakan di panelnya, bukan disembunyikan. Kedalamannya mengikuti Interval
 * di rel kiri: sebuah panel yang diam-diam berangkat dari "seluruh kedalaman"
 * ketika rel kirinya berbunyi "Sand A" memulangkan Rw dari batuan yang tidak
 * sedang ditanyakan siapa pun.
 *
 * Daftar sumurnya mengikuti **sumur yang sedang aktif**, satu nama dan bukan
 * seluruh centang di rel kiri. Rel itu memilih sumur yang digolongkan, dan di
 * Prabumulih itu lima ratus sekaligus; sebuah crossplot yang berangkat dari
 * kelima ratusnya menjawab pertanyaan yang tidak ditanyakan siapa pun di sini
 * — Rw rata-rata seluruh lapangan, yaitu satu angka atas belasan akuifer yang
 * berlainan. Yang ditanyakan adalah Rw di sumur yang sedang dibaca. Sumur
 * kedua dan ketiga ditambahkan dengan tangan, satu centang pada satu nama,
 * sebab yang menambahkannya tahu sumur mana yang berbagi kaki air dengannya —
 * dan daftar rel kiri tetap bisa dipanggil sekaligus lewat pintasannya.
 */

import { useMemo, useState } from "react";

import type { LrlcCatalogue, Screening, Well } from "../types";
import {
  boundText,
  boundWells,
  loggedWells,
  markerRows,
  ScreeningTable,
  StackedDropdown,
  withChosen,
  zoneNames,
} from "./DataScope";
import ScreeningMap from "./ScreeningMap";
import { Checkbox, Chip, Empty, NumberField, OptionalNumberField,
         Notice, Panel, Segmented } from "./ui";

/** Sumur mana: satu, beberapa yang dipilih sendiri, atau yang di sekelilingnya. */
export type RwWellMode = "single" | "many" | "around";

/** Kedalaman mana: seluruhnya, antar marker, zona bernama, atau MD. */
export type RwDepthMode = "all" | "markers" | "zones" | "md";

export interface RwScope {
  mode: RwWellMode;
  /** Sumur tunggal pada mode `single`. */
  single: number | null;
  /**
   * Daftar sumur pada mode `many`. `null` berarti *belum disentuh*, dan yang
   * dipakai adalah sumur yang sedang aktif — bukan daftar kosong, yang di sini
   * akan berarti seluruh proyek.
   */
  many: number[] | null;
  /** Sumur yang sedang dianalisis: pusat lingkarannya pada mode `around`. */
  center: number | null;
  /** Jari-jari, diketik dalam kilometer dan dikirim dalam meter. */
  radiusKm: number;
  radiusMode: "surface" | "trajectory";
  /** `null` berarti mengikuti Interval di rel kiri layar. */
  depth: RwDepthMode | null;
  topRef: string;
  baseRef: string;
  zones: string[];
  mdMin: number | null;
  mdMax: number | null;
}

export const DEFAULT_RW_SCOPE: RwScope = {
  mode: "many",
  single: null,
  many: null,
  center: null,
  radiusKm: 5,
  radiusMode: "surface",
  depth: null,
  topRef: "",
  baseRef: "",
  zones: [],
  mdMin: null,
  mdMax: null,
};

/** Apa yang diikuti panel ini selama pemilihnya belum disentuh. */
export interface RwFollowing {
  /**
   * Sumur yang sedang aktif di layar — yang dibaca daftar sumur ini selama
   * belum disentuh. `null` hanya ketika layar memang belum punya sumur aktif
   * yang bisa dihitung, dan di situlah rel kiri dipakai sebagai gantinya.
   */
  well: number | null;
  /** Sumur yang tercentang di rel kiri layar, ditawarkan sebagai pintasan. */
  wells: number[];
  /** Interval yang terpilih di rel kiri layar; "" berarti seluruh kedalaman. */
  zone: string;
}

/** Kedalaman yang benar-benar berlaku, setelah rel kiri diperhitungkan. */
export function rwDepthMode(scope: RwScope, following: RwFollowing): RwDepthMode {
  return scope.depth ?? (following.zone ? "zones" : "all");
}

/** Zona yang benar-benar berlaku, dengan alasan yang sama. */
export function rwZones(scope: RwScope, following: RwFollowing): string[] {
  if (scope.depth !== null) return scope.zones;
  return following.zone ? [following.zone] : [];
}

/**
 * Sumur yang benar-benar diikuti selama daftarnya belum disentuh.
 *
 * Sumur yang sedang aktif, dan rel kiri hanya sebagai jaring pengaman: sebuah
 * daftar kosong di sini berangkat ke server sebagai permintaan atas seluruh
 * proyek, yaitu kebalikan persis dari satu sumur.
 */
function rwFollowed(following: RwFollowing): number[] {
  return following.well === null ? following.wells : [following.well];
}

/** Sumur yang benar-benar diminta; daftar kosong berarti seluruh proyek. */
export function rwWells(scope: RwScope, following: RwFollowing): number[] {
  if (scope.mode === "single") return scope.single === null ? [] : [scope.single];
  // Di sekeliling sebuah sumur, yang dijaring adalah seluruh lapangan: sebuah
  // lingkaran yang lebih dulu dipotong oleh daftar centang di rel kiri hanya
  // bisa memulangkan sumur yang sudah ada di daftar itu, dan pertanyaannya
  // justru tentang sumur yang tidak ada di sana.
  if (scope.mode === "around") return [];
  return scope.many ?? rwFollowed(following);
}

/**
 * Lingkup ini sebagai badan permintaan, dalam ejaan yang dimengerti server.
 *
 * Yang mati tidak dikirim sebagai nilai ujungnya melainkan tidak dikirim sama
 * sekali: `md_min: 0` menyuruh server memotong pada kedalaman nol, dan itu
 * jawaban yang berbeda dari "jangan dipotong".
 */
export function rwScopeBody(
  scope: RwScope,
  following: RwFollowing,
): Record<string, unknown> {
  const depth = rwDepthMode(scope, following);
  return {
    wells: rwWells(scope, following),
    zones: depth === "zones" ? rwZones(scope, following) : [],
    // Dikosongkan dengan sengaja: server masih menerima `zone` tunggal demi
    // ketiga layar LRLC yang lain, dan sebuah nama yang tertinggal di sini
    // akan mempersempit selang yang panelnya berkata terbuka.
    zone: "",
    top_ref: depth === "markers" ? scope.topRef : "",
    base_ref: depth === "markers" ? scope.baseRef : "",
    md_min: depth === "md" ? scope.mdMin : null,
    md_max: depth === "md" ? scope.mdMax : null,
    center_well: scope.mode === "around" ? scope.center : null,
    radius: scope.mode === "around" ? Math.round(scope.radiusKm * 1000) : null,
    radius_mode: scope.mode === "around" ? scope.radiusMode : null,
  };
}

type CatalogueWell = LrlcCatalogue["wells"][number];

export default function LrlcRwScope({
  scope,
  onChange,
  wells,
  field,
  following,
  screening,
}: {
  scope: RwScope;
  onChange: (next: RwScope) => void;
  /** Sumur yang sudah dibor, sebagaimana dikenal katalog LRLC. */
  wells: CatalogueWell[];
  /** Sumur lapangan berikut marker dan zonanya. */
  field: Well[];
  following: RwFollowing;
  /** Hasil jaring dari muatan terakhir, atau null ketika jaringnya mati. */
  screening: Screening | null;
}) {
  const [query, setQuery] = useState("");
  const set = (patch: Partial<RwScope>) => onChange({ ...scope, ...patch });

  const zones = useMemo(() => zoneNames(field), [field]);
  // Hanya marker sumur yang berkurva: sumur tanpa kurva sudah gugur sebelum
  // selangnya sempat dipotong, dan sebuah nama yang tidak sanggup menyumbang
  // satu sampel pun adalah pilihan yang menjanjikan sesuatu yang tak dimiliki.
  const markers = useMemo(() => markerRows(loggedWells(field)), [field]);
  const bounds = useMemo(
    () => markers.map((row) => ({ value: `marker:${row.name}`, label: row.name })),
    [markers],
  );

  const depth = rwDepthMode(scope, following);
  const picked = rwWells(scope, following);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return needle ? wells.filter((w) => w.name.toLowerCase().includes(needle)) : wells;
  }, [wells, query]);
  const chosen = useMemo(() => new Set(picked), [picked]);

  /** Sumur pertama yang benar-benar berkurva — titik mula yang masuk akal. */
  const firstLogged = (wells.find((w) => w.logged) ?? wells[0])?.id ?? null;

  /**
   * Pilihan sumur untuk kedua pemilih tunggal panel ini.
   *
   * Idnya dieja sebagai teks sebab pemilihnya `StackedDropdown` — label di
   * atas kotaknya, bukan di sampingnya. `Dropdown` berlabel samping menaruh
   * label dan tombolnya di satu baris lentur, dan tombol yang diminta selebar
   * 100% di sana menjadi selebar kolomnya **ditambah** labelnya: ia menjorok
   * ke kolom sebelah dan menimpa kalimat yang sedang berdiri di sana.
   */
  const wellOptions = wells.map((w) => ({
    value: String(w.id),
    label: w.logged ? w.name : `${w.name} — no curve`,
  }));

  /** Nama sumur yang sedang aktif, untuk kalimat yang menyebutkannya. */
  const activeName = wells.find((w) => w.id === following.well)?.name ?? null;

  const toggleWell = (id: number) => {
    const base = scope.many ?? rwFollowed(following);
    set({ many: base.includes(id) ? base.filter((v) => v !== id) : [...base, id] });
  };

  const toggleZone = (name: string) => {
    const base = rwZones(scope, following);
    set({
      depth: "zones",
      zones: base.includes(name) ? base.filter((v) => v !== name) : [...base, name],
    });
  };

  return (
    <Panel
      title="Data read for Rw and RO"
      meta={
        scope.mode === "around"
          ? `${scope.radiusKm} km around ${
              wells.find((w) => w.id === scope.center)?.name ?? "—"}`
          : `${picked.length || wells.length} ${
              picked.length === 1 ? "well" : "wells"}`
      }
    >
      <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 7,
                      flex: "1 1 280px", minWidth: 250 }}>
          <span style={label}>Wells</span>
          <Segmented
            value={scope.mode}
            size="sm"
            options={[
              { value: "single", label: "One well" },
              { value: "many", label: "Several" },
              { value: "around", label: "Around one" },
            ]}
            onChange={(mode) =>
              set({
                mode: mode as RwWellMode,
                single: scope.single ?? firstLogged,
                center: scope.center ?? firstLogged,
              })
            }
          />

          {scope.mode === "single" && (
            <>
              <StackedDropdown
                label="WELL"
                value={String(scope.single ?? firstLogged ?? 0)}
                options={wellOptions}
                onChange={(single) => set({ single: Number(single) })}
              />
              <span style={hint}>
                One well is the honest answer only where its own water leg is
                long enough. Below the sample count this plot will read, it
                says so rather than drawing a line through twenty points.
              </span>
            </>
          )}

          {scope.mode === "many" && (
            <>
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search wells…"
                aria-label="Search wells for the Rw crossplot"
                style={search}
              />
              {/* Pintasannya tiga, dan hanya yang pertama sebuah *keadaan*:
                  ia mengikuti sumur aktif dan ikut berpindah bersamanya.
                  Kedua yang lain menyalin sebuah daftar ke tangan sekali
                  saja — sesudahnya daftar itu milik panel ini, dan rel kiri
                  yang bergeser tidak lagi menggesernya diam-diam. */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Chip
                  active={scope.many === null}
                  onClick={() => set({ many: null })}
                >
                  Follow the active well
                </Chip>
                <Chip
                  active={false}
                  onClick={() => set({ many: following.wells })}
                >
                  Wells ticked on the left
                </Chip>
                <Chip
                  active={false}
                  onClick={() => set({ many: wells.filter((w) => w.logged)
                                                  .map((w) => w.id) })}
                >
                  Every logged well
                </Chip>
              </div>
              <div
                className="scroll-y"
                style={{ maxHeight: 150, display: "flex", flexDirection: "column" }}
              >
                {rows.map((well) => (
                  <Checkbox
                    key={well.id}
                    checked={chosen.has(well.id)}
                    label={well.name}
                    dot={well.color || undefined}
                    meta={well.logged ? undefined : "no curve"}
                    onChange={() => toggleWell(well.id)}
                  />
                ))}
                {!rows.length && <Empty>No well matches.</Empty>}
              </div>
              {scope.many === null && (
                <span style={hint}>
                  {activeName
                    ? `Following the active well — ${activeName} alone. `
                    : `Following the Wells panel on the left — `
                      + `${following.wells.length} ticked there. `}
                  Tick another name to read its water leg too; this list then
                  becomes its own and stops following.
                </span>
              )}
              {/* Daftar yang dikosongkan dengan tangan **tidak** berarti
                  "tidak ada sumur": server membaca daftar kosong sebagai
                  seluruh proyek, dan sejak yang tercentang di sini tinggal
                  satu, satu klik sudah cukup untuk sampai ke sana. Yang
                  terjadi disebutkan, bukan dibiarkan terlihat seperti
                  crossplot yang kebetulan menjadi lebih ramai. */}
              {scope.many?.length === 0 && (
                <Notice tone="warn">
                  Not one well is ticked, so this reads every well in the
                  project — Rw from a dozen aquifers at once. Tick the wells
                  its water leg should come from.
                </Notice>
              )}
            </>
          )}

          {scope.mode === "around" && (
            <>
              <StackedDropdown
                label="CENTRE"
                value={String(scope.center ?? firstLogged ?? 0)}
                options={wellOptions}
                onChange={(center) => set({ center: Number(center) })}
              />
              <NumberField
                label="Radius"
                unit="km"
                value={scope.radiusKm}
                min={0.01}
                step={0.5}
                onChange={(radiusKm) => set({ radiusKm })}
              />
              <Segmented
                value={scope.radiusMode}
                size="sm"
                options={[
                  { value: "surface", label: "Well head" },
                  { value: "trajectory", label: "Along the track" },
                ]}
                onChange={(radiusMode) =>
                  set({ radiusMode: radiusMode as RwScope["radiusMode"] })}
              />
              <span style={hint}>
                {scope.radiusMode === "trajectory"
                  ? "The centre descends along the reference well's track, so a "
                    + "neighbour can be caught at certain depths only. Formation "
                    + "water changes with place: a neighbour that shares the "
                    + "aquifer is what this asks for."
                  : "One circle at the head of the well being analysed. Every "
                    + "well that falls inside it contributes its whole depth, "
                    + "and the table below names the ones that did not."}
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 7,
                      flex: "1 1 280px", minWidth: 250 }}>
          <span style={label}>Depth</span>
          <Segmented
            value={depth}
            size="sm"
            options={[
              { value: "all", label: "Every depth" },
              { value: "markers", label: "Markers" },
              { value: "zones", label: "Zones" },
              { value: "md", label: "MD" },
            ]}
            onChange={(next) => set({ depth: next as RwDepthMode })}
          />

          {depth === "markers" &&
            (bounds.length ? (
              <>
                <div style={{ display: "flex", gap: 8 }}>
                  <StackedDropdown
                    label="TOP"
                    value={scope.topRef}
                    options={[{ value: "", label: "Start of data" },
                              ...withChosen(bounds, scope.topRef,
                                            `${boundText(scope.topRef)} — no curve`)]}
                    onChange={(topRef) => set({ depth: "markers", topRef })}
                  />
                  <StackedDropdown
                    label="BASE"
                    value={scope.baseRef}
                    options={[{ value: "", label: "Next marker" },
                              ...withChosen(bounds, scope.baseRef,
                                            `${boundText(scope.baseRef)} — no curve`)]}
                    onChange={(baseRef) => set({ depth: "markers", baseRef })}
                  />
                </div>
                <span style={hint}>
                  {[scope.topRef, scope.baseRef]
                    .filter(Boolean)
                    .map((ref) => `${boundText(ref)} in ${boundWells(field, ref)}`)
                    .join("; ") || "Both ends open — the whole depth of every well"}
                  . Each well uses its own marker depth; a well missing one of
                  them is skipped and named. An empty base means the next marker
                  below the top, in that well.
                </span>
              </>
            ) : (
              <Notice tone="warn">
                No marker sits on a well that carries curves, so none can bound
                this plot. Use MD, or set the markers first in the Data menu.
              </Notice>
            ))}

          {depth === "zones" &&
            (zones.length ? (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {zones.map((zone) => (
                    <Chip
                      key={zone.name}
                      active={rwZones(scope, following).includes(zone.name)}
                      color={zone.color}
                      onClick={() => toggleZone(zone.name)}
                    >
                      {zone.name}
                    </Chip>
                  ))}
                </div>
                {!rwZones(scope, following).length && (
                  <span style={hint}>
                    No zone picked yet — the whole depth of every well is read.
                  </span>
                )}
              </>
            ) : (
              <Empty>This field has no zone yet.</Empty>
            ))}

          {depth === "md" && (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField
                    label="MD MIN"
                    value={scope.mdMin}
                    step={1}
                    onChange={(mdMin) => set({ depth: "md", mdMin })}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <OptionalNumberField
                    label="MD MAX"
                    value={scope.mdMax}
                    step={1}
                    onChange={(mdMax) => set({ depth: "md", mdMax })}
                  />
                </div>
              </div>
              <span style={hint}>
                A depth written as a number is the same depth in every well.
                Where the interval is meant to follow the rock, a marker or a
                zone says it better.
              </span>
            </>
          )}

          {scope.depth === null && (
            <span style={hint}>
              Following the Interval on the left —{" "}
              {following.zone || "every depth"}. Touch this row and it becomes
              its own.
            </span>
          )}
        </div>
      </div>

      {screening && screening.wells.length > 0 && (
        <>
          {/* Peta dan tabel berdampingan, dan keduanya dibutuhkan.
              Tabel menjawab "berapa titik dari KAG-014"; peta menjawab
              pertanyaan yang ditanyakan lebih dulu, sebelum radiusnya disetel:
              apakah yang terjaring mengelilingi sumurnya atau berkerumun di
              satu sisinya saja — sebab Rw dari setengah lingkaran adalah Rw
              dari setengah akuifer. */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 14,
                        alignItems: "flex-start" }}>
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <ScreeningMap
                screening={screening}
                wells={field.map((well) => ({
                  id: well.id, name: well.name,
                  x: well.surface_x, y: well.surface_y,
                }))}
              />
            </div>
            <div style={{ flex: "1 1 320px", minWidth: 280,
                          display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={label}>
                Caught — {screening.kept} of {screening.asked} wells, nearest first
              </span>
              {/* Dua ratus dan bukan dua puluh. Jaring ini boleh meloloskan
                  enam puluh sumur — itu batasnya — dan tabel yang berhenti di
                  baris kedua puluh menyembunyikan dua pertiga dari yang
                  justru sedang dibaca. Sisanya, yang tidak lolos, adalah
                  tetangga yang nyaris: merekalah yang menentukan radiusnya
                  digeser ke mana. Wadahnya bergulir; yang dibatasi di sini
                  hanyalah berapa baris yang dibuat. */}
              <ScreeningTable screening={screening} maxRows={200} ranked />
            </div>
          </div>
          {screening.note && <Notice tone="warn">{screening.note}</Notice>}
        </>
      )}
    </Panel>
  );
}

const label: React.CSSProperties = {
  font: "600 9.5px var(--font-mono)", color: "var(--ink-5)",
  letterSpacing: "0.04em", textTransform: "uppercase",
};

const hint: React.CSSProperties = {
  font: "400 9.5px/1.6 var(--font-sans)", color: "var(--ink-6)",
};

const search: React.CSSProperties = {
  height: 26, padding: "0 8px", minWidth: 0,
  background: "var(--panel-inset)",
  border: "1px solid var(--line-strong)",
  borderRadius: "var(--radius)",
  font: "400 11px var(--font-sans)", color: "var(--ink)", outline: "none",
};
